import React, { useState } from "react";
import styles from "../stylesER.module.scss";

// Grupo colapsable (Agencia / Broker / Programática / APX). Header clickeable con título, conteo
// de facturas y total del grupo; la tabla se muestra solo si está expandido. Display puro (10.48).
const SeccionColapsable = ({ titulo, count, totalFmt, abierto, onToggle, children }) => (
  <div className={styles.modalGrupo}>
    <button type="button" className={styles.modalGrupoHeader} onClick={onToggle}>
      <span className={styles.modalGrupoChevron}>{abierto ? "▼" : "►"}</span>
      <span className={styles.modalGrupoTitulo}>{titulo}</span>
      <span className={styles.modalGrupoCount}>
        {count} {count === 1 ? "factura" : "facturas"}
      </span>
      <span className={styles.modalGrupoTotal}>{totalFmt}</span>
    </button>
    {abierto && <div className={styles.modalGrupoBody}>{children}</div>}
  </div>
);

export const AgencyCommissionModal = ({
  isOpen,
  monthIndex,
  mesesCompletos,
  comisiones,
  comisionesBroker = [],
  costosProgramaticos = [],
  apx = [],
  oportunidades,
  formatCurrency,
  pais,
  onClose,
}) => {
  // Estado de colapso por grupo (en memoria, sin storage). Default: todos expandidos → nada se
  // oculta al abrir; el usuario colapsa lo que no le interesa. El scroll interno maneja el largo.
  const [abiertos, setAbiertos] = useState({ agencia: true, broker: true, programatica: true, apx: true });
  const toggle = (k) => setAbiertos((p) => ({ ...p, [k]: !p[k] }));

  if (!isOpen || monthIndex === null) return null;

  // Contratos traen c.cliente directo; PAUTA lo resuelve por facturanumero en oportunidades.
  const clienteDeFactura = (c) =>
    c.cliente || oportunidades.find((o) => o.facturanumero === c.facturanumero)?.cliente || "—";

  const locale = pais === "Chile" ? "es-CL" : "es-PE";
  const importeCell = (c) =>
    c.moneda_dolar
      ? `US$ ${(c.importeOriginalMoneda || 0).toLocaleString(locale, { maximumFractionDigits: 0 })}`
      : formatCurrency(c.importe);

  // Total combinado = el que "manda" y coincide con la línea del ER
  const totalAgencia = comisiones.reduce((s, c) => s + c.comision, 0);
  const totalBroker = comisionesBroker.reduce((s, c) => s + c.comisionBroker, 0);
  const totalProgramatico = costosProgramaticos.reduce((s, c) => s + (c.takeLocal || 0), 0);
  const totalApx = apx.reduce((s, c) => s + (c.apxLocal || 0), 0);
  const totalCombinado = totalAgencia + totalBroker + totalProgramatico + totalApx;
  const hayEntries =
    comisiones.length > 0 || comisionesBroker.length > 0 || costosProgramaticos.length > 0 || apx.length > 0;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalIntermContent} onClick={(e) => e.stopPropagation()}>
        {/* ── Header fijo ── */}
        <div className={styles.modalIntermHeader}>
          <h3>Costo intermediación - {mesesCompletos[monthIndex]}</h3>
        </div>

        {/* ── Body scrolleable (grupos colapsables) ── */}
        <div className={styles.modalIntermBody}>
          {/* ── Agencia ── */}
          {comisiones.length > 0 && (
            <SeccionColapsable
              titulo="Agencia"
              count={comisiones.length}
              totalFmt={formatCurrency(totalAgencia)}
              abierto={abiertos.agencia}
              onToggle={() => toggle("agencia")}
            >
              <table className={styles.modalTable}>
                <thead>
                  <tr>
                    <th className={styles.agencyFacturaHeader}>Factura</th>
                    <th className={styles.agencyClienteHeader}>Cliente</th>
                    <th className={styles.agencyIntermediarioHeader}>Intermediario</th>
                    <th className={styles.agencyImporteHeader}>Importe</th>
                    <th className={styles.agencyPorcentajeHeader}>%</th>
                    <th className={styles.agencyComisionHeader}>Comisión</th>
                  </tr>
                </thead>
                <tbody>
                  {comisiones.map((c, i) => (
                    <tr key={i}>
                      <td className={styles.agencyFacturaData}>{c.facturanumero}</td>
                      <td className={styles.agencyClienteData}>{clienteDeFactura(c)}</td>
                      <td className={styles.agencyIntermediarioData}>{c.intermediario}</td>
                      <td className={styles.agencyImporteData}>{importeCell(c)}</td>
                      <td className={styles.agencyPorcentajeData}>{c.porcentaje}%</td>
                      <td className={styles.agencyComisionData}>{formatCurrency(c.comision)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SeccionColapsable>
          )}

          {/* ── Broker (solo si hay entries) ── */}
          {comisionesBroker.length > 0 && (
            <SeccionColapsable
              titulo="Broker"
              count={comisionesBroker.length}
              totalFmt={formatCurrency(totalBroker)}
              abierto={abiertos.broker}
              onToggle={() => toggle("broker")}
            >
              <table className={styles.modalTable}>
                <thead>
                  <tr>
                    <th className={styles.agencyFacturaHeader}>Factura</th>
                    <th className={styles.agencyClienteHeader}>Cliente</th>
                    <th className={styles.agencyIntermediarioHeader}>Broker</th>
                    <th className={styles.agencyImporteHeader}>Importe</th>
                    <th className={styles.agencyPorcentajeHeader}>%</th>
                    <th className={styles.agencyComisionHeader}>Comisión</th>
                  </tr>
                </thead>
                <tbody>
                  {comisionesBroker.map((c, i) => (
                    <tr key={i}>
                      <td className={styles.agencyFacturaData}>{c.facturanumero}</td>
                      <td className={styles.agencyClienteData}>{clienteDeFactura(c)}</td>
                      <td className={styles.agencyIntermediarioData}>{c.brokerNombre}</td>
                      <td className={styles.agencyImporteData}>{importeCell(c)}</td>
                      <td className={styles.agencyPorcentajeData}>{c.porcentajeBroker}%</td>
                      <td className={styles.agencyComisionData}>{formatCurrency(c.comisionBroker)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SeccionColapsable>
          )}

          {/* ── Programática (take del exchange — solo si hay entries) ── */}
          {costosProgramaticos.length > 0 && (
            <SeccionColapsable
              titulo="Programática"
              count={costosProgramaticos.length}
              totalFmt={formatCurrency(totalProgramatico)}
              abierto={abiertos.programatica}
              onToggle={() => toggle("programatica")}
            >
              <table className={styles.modalTable}>
                <thead>
                  <tr>
                    <th className={styles.agencyFacturaHeader}>Factura</th>
                    <th className={styles.agencyClienteHeader}>Cliente</th>
                    <th className={styles.agencyIntermediarioHeader}>Exchange</th>
                    <th className={styles.agencyImporteHeader}>Importe</th>
                    <th className={styles.agencyPorcentajeHeader}>%</th>
                    <th className={styles.agencyComisionHeader}>Take</th>
                  </tr>
                </thead>
                <tbody>
                  {costosProgramaticos.map((c, i) => (
                    <tr key={i}>
                      <td className={styles.agencyFacturaData}>{c.facturanumero}</td>
                      <td className={styles.agencyClienteData}>{clienteDeFactura(c)}</td>
                      <td className={styles.agencyIntermediarioData}>Place Exchange</td>
                      <td className={styles.agencyImporteData}>{importeCell(c)}</td>
                      <td className={styles.agencyPorcentajeData}>
                        {c.importe > 0 ? `${((c.takeLocal / c.importe) * 100).toFixed(1)}%` : "—"}
                      </td>
                      <td className={styles.agencyComisionData}>{formatCurrency(c.takeLocal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SeccionColapsable>
          )}

          {/* ── APX (Publicis — solo si hay entries) ── */}
          {apx.length > 0 && (
            <SeccionColapsable
              titulo="APX (Publicis)"
              count={apx.length}
              totalFmt={formatCurrency(totalApx)}
              abierto={abiertos.apx}
              onToggle={() => toggle("apx")}
            >
              <table className={styles.modalTable}>
                <thead>
                  <tr>
                    <th className={styles.agencyFacturaHeader}>Factura</th>
                    <th className={styles.agencyClienteHeader}>Cliente</th>
                    <th className={styles.agencyIntermediarioHeader}>Acuerdo</th>
                    <th className={styles.agencyImporteHeader}>Importe</th>
                    <th className={styles.agencyPorcentajeHeader}>%</th>
                    <th className={styles.agencyComisionHeader}>APX</th>
                  </tr>
                </thead>
                <tbody>
                  {apx.map((c, i) => (
                    <tr key={i}>
                      <td className={styles.agencyFacturaData}>{c.facturanumero}</td>
                      <td className={styles.agencyClienteData}>{clienteDeFactura(c)}</td>
                      <td className={styles.agencyIntermediarioData}>APX</td>
                      <td className={styles.agencyImporteData}>{importeCell(c)}</td>
                      <td className={styles.agencyPorcentajeData}>{c.porcentaje}%</td>
                      <td className={styles.agencyComisionData}>{formatCurrency(c.apxLocal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SeccionColapsable>
          )}
        </div>

        {/* ── Footer fijo: total intermediación siempre visible + cerrar ── */}
        <div className={styles.modalIntermFooter}>
          {hayEntries ? (
            <div className={styles.modalIntermTotalPie}>
              <span className={styles.modalIntermTotalLabel}>Total intermediación</span>
              <span className={styles.modalIntermTotalValor}>{formatCurrency(totalCombinado)}</span>
            </div>
          ) : (
            <span />
          )}
          <button className={styles.btnClose} onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};
